/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  AlertBanner,
  Checkbox,
  Icon,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import { getMessageKey } from '../../../utils/utils';
import AriaErrors from '../../AriaErrors';
import { getCertKind } from '../../CertsDetailsList/Tile/utils';
import CertsModalCommentBox, {
  useCertsModalCommentErrors,
} from '../../CertsModalCommentBox/CertsModalCommentBox';
import CertsUserListDropdown from '../../CertsUserListDropdown';
import { LINE_ITEM_TYPES } from '../CertsActionButtonsGroup/constants';
import { CERT_ENTITLEMENT_VALUEKEY } from '../constant';

import {
  ACCOUNT_DISPLAY_NAME,
  CHILD_ENTITLEMENT_DISPLAY_NAME,
  ENTITLEMENT_DISPLAY_NAME,
  EO_ACCOUNT_DISPLAY_NAME,
  EO_CHILD_ENTITLEMENT_DISPLAY_NAME,
  EO_ENTITLEMENT_DISPLAY_NAME,
  ROLE_DISPLAY_NAME,
  USER_DISPLAY_NAME,
} from './constants';
import { isEmptyObject } from './utils';

function CertsReassignContent({
  nodeData,
  setIsIncludedAssociateChecked,
  isIncludeAssociateChecked,
  handleCommentDescription,
  setSelectedUser,
  selectedUser,
  reassignModalDetails,
  dropdownClassName,
  isCommentRequired,
  commentsDetails,
  isSelectUserCritical,
  setIsSelectUserCritical,
  isMobileWidth,
  showCertifier,
}) {
  const intl = useIntl();
  const {
    global: { certKey },
    lineActions: { errorMessage, isActionError, reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);
  const commentErrors = useCertsModalCommentErrors(
    commentsDetails?.commentDescription
  );
  const [dropdownError, setDropdownError] = useState('');
  const { predefinedComments: { allowEditPredefinedComments = false } = {} } =
    useSelector((state) => state?.certifications?.details?.core || {});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(isActionError);
  }, [isActionError]);

  const isCertsKindUser =
    nodeData?.CERT_ENTITLEMENT_VALUEKEY === CERT_ENTITLEMENT_VALUEKEY.USER;

  const CertifierClassNames = classnames(
    'CertsReassignModal-containerCertifierSelection',
    !showCertifier &&
      'CertsReassignModal-containerCertifierSelection--noCertifier'
  );

  /** to get lineitem display name */
  const getSelectedLineItemName = (lineItemType) => {
    let name = '';

    switch (lineItemType) {
      case LINE_ITEM_TYPES.USER:
        name = nodeData[USER_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ACCOUNT:
        name =
          nodeData[ACCOUNT_DISPLAY_NAME] || nodeData[EO_ACCOUNT_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ROLE:
        name = nodeData[ROLE_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.ENTITLEMENT:
        name =
          nodeData[ENTITLEMENT_DISPLAY_NAME] ||
          nodeData[EO_ENTITLEMENT_DISPLAY_NAME];
        break;
      case LINE_ITEM_TYPES.CHILD_ENTITLEMENT:
        name =
          nodeData[CHILD_ENTITLEMENT_DISPLAY_NAME] ||
          nodeData[EO_CHILD_ENTITLEMENT_DISPLAY_NAME];
        break;
      default:
        break;
    }

    if (!name) name = 'Not Provided';

    return name;
  };

  return (
    <div>
      <div className='CertsReassignModal-containerHeader'>
        <AlertBanner
          className='reassignCertifier-selectionWarn'
          colorTheme='Critical'
          title='Error'
          description={errorMessage}
          isVisible={isVisible}
          onCancel={() => setIsVisible(false)}
        />
        <div className='CertsReassignModal-details'>
          <Typography
            kind='h4'
            mobile='h4-d'
            className='CertsReassignModal-containerTitle'>
            {`${
              messages.REASSSIGN_SELECTED ||
              localize(intl, getMessageKey('REASSSIGN_SELECTED'))
            } ${reassignModalDetails?.lineItemKind}`}
          </Typography>
          {/* only for user-> accounts ->entitilements  when reassign for accounts */}
          {reassignModalDetails?.isNoteRequired && (
            <div className='CertsReassignModal-containerNote'>
              <Tooltip
                openDelay={500}
                text={reassignModalDetails?.noteText}
                maxWidth='20rem'
                placement={isMobileWidth ? 'top' : 'right'}
                offset={8}
                trigger={
                  <Icon
                    kind='AlertInfoFilledSeparateColors'
                    color='--color-foreground-info-medium'
                    className='CertsReassignModal-containerNoteIcon'
                  />
                }
              />
            </div>
          )}
          {/* only for user-> accounts ->entitilements  when reassign for accounts */}
        </div>

        <div className='CertsReassignModal-container-userDetails'>
          {isCertsKindUser ? (
            <Icon
              size='smallMedium'
              kind='User'
              color='--color-foreground-secondary-bold'
            />
          ) : (
            <Icon
              kind={getCertKind(nodeData)}
              color='--color-foreground-secondary-bold'
              size='smallMedium'
            />
          )}
          <Typography
            kind='h4'
            mobile='h4-d'
            className='CertsReassignModal-container-userDetailsName'>
            {getSelectedLineItemName(reassignModalDetails?.lineItemKind)}
          </Typography>
        </div>
        {reassignModalDetails?.isIncludeAssociatedEnt && (
          <div className='CertsReassignModal-includeCheckbox'>
            <Checkbox
              id='reaassign-content-checkbox-2'
              name='checkbox2'
              kind='singleLine'
              dataTestId='checkbox2'
              isChecked={isIncludeAssociateChecked}
              onChange={(event) =>
                setIsIncludedAssociateChecked(event.target.checked)
              }
              label={reassignModalDetails?.isIncludeAsscLabel}
            />
          </div>
        )}
      </div>
      <div className={CertifierClassNames}>
        {showCertifier && (
          <>
            {!selectedUser && (
              <div className='CertsReassignModal-DropdownAsterisk'>*</div>
            )}
            <Typography
              kind='h4'
              mobile='h4-d'
              className='CertsReassignModal-container-certifier'>
              {messages.REASSIGN_SELECT_CERTIFIER ||
                localize(intl, getMessageKey('REASSIGN_SELECT_CERTIFIER'))}
            </Typography>
            <CertsUserListDropdown
              className={dropdownClassName}
              defaultReassignee={reassignConfigMap?.defaultReassignee}
              defaultAssigneName={reassignConfigMap?.defaultReassigneeName}
              onSelect={(user) => {
                if (user && !isEmptyObject(user)) {
                  setSelectedUser(user);
                } else {
                  setSelectedUser(null);
                }

                setIsSelectUserCritical(false);
              }}
              selectedUser={selectedUser}
              certKey={certKey}
              showDefaultCertifier
              isCritical={isSelectUserCritical}
              placeholder={
                messages.REASSSIGN_PLACEHOLDER_LABEL ||
                localize(intl, getMessageKey('REASSSIGN_PLACEHOLDER_LABEL'))
              }
              certifierUserKey={reassignConfigMap?.certifierUserKey}
              setErrorMessage={setDropdownError}
            />
          </>
        )}
      </div>
      {isCommentRequired && (
        <CertsModalCommentBox
          defaultComments={commentsDetails?.predefComments}
          showAlert={commentsDetails.showAlert}
          onChange={handleCommentDescription}
          className='CertsReassignModal-comments'
          label={
            messages['comments.ATTESTATIONS'] ||
            localize(intl, getMessageKey('comments.ATTESTATIONS'))
          }
          allowEditPredefinedComments={allowEditPredefinedComments}
        />
      )}
      <AriaErrors
        isCritical={
          (isSelectUserCritical && showCertifier) || commentsDetails.showAlert
        }
        messages={[
          isSelectUserCritical && dropdownError,
          ...(commentsDetails.showAlert ? commentErrors : []),
        ]}
      />
    </div>
  );
}

CertsReassignContent.propTypes = {
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  setIsIncludedAssociateChecked: PropTypes.func.isRequired,
  isIncludeAssociateChecked: PropTypes.bool.isRequired,
  handleCommentDescription: PropTypes.func.isRequired,
  selectedUser: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  setSelectedUser: PropTypes.func.isRequired,
  reassignModalDetails: PropTypes.shape({
    isIncludeAssociatedEnt: PropTypes.bool,
    isNoteRequired: PropTypes.bool,
    noteText: PropTypes.string,
    isIncludeAsscLabel: PropTypes.string,
    actionKind: PropTypes.string,
    lineItemKind: PropTypes.string,
  }),
  dropdownClassName: PropTypes.string.isRequired,
  isCommentRequired: PropTypes.bool.isRequired,
  commentsDetails: PropTypes.shape({
    commentDescription: PropTypes.string.isRequired,
    predefComments: PropTypes.arrayOf(PropTypes.string), // Optional array of strings
    isCommentApplied: PropTypes.bool.isRequired,
    showAlert: PropTypes.bool.isRequired,
  }).isRequired,
  isSelectUserCritical: PropTypes.bool.isRequired,
  setIsSelectUserCritical: PropTypes.func.isRequired,
  isMobileWidth: PropTypes.bool.isRequired,
  showCertifier: PropTypes.bool.isRequired,
};

CertsReassignContent.defaultProps = {
  reassignModalDetails: {},
};

export default CertsReassignContent;
